/**
 * EnrollScreen.jsx — Step 6.
 *
 * Three tiny screens on the anonymous-visitor flow, all keyed off the
 * URL hash:
 *
 *   #enroll                 → <EnrollForm>       (4-field enrollment form)
 *   #enroll/success?session_id=...
 *                           → <EnrollSuccess>    ("Check your email" screen)
 *   #enroll/cancel          → <EnrollCancel>     ("Payment cancelled" retry)
 *
 * Form flow:
 *   1. User fills association_name, admin_full_name, admin_email, unit_count
 *   2. Client-side validation mirrors the backend Pydantic validators
 *      (min 2 chars on names, valid email, unit_count ≥ 1)
 *   3. POST /api/enroll/checkout with the fields + current origin
 *   4. Server returns `{ checkout_url, session_id }`
 *   5. Full-page redirect to Stripe's hosted checkout
 *   6. After payment, Stripe redirects to `#enroll/success?session_id=…`
 *      which shows a "Check your email" screen — the actual account
 *      provisioning happens server-side via the webhook (Step 3) and
 *      lands in the user's inbox as an invite email with a temp
 *      password.
 */
import React from "react";


const NAVY = "#1C4E80";
const SKY = "#0091D5";
const CORAL = "#EA6A47";
const SLATE = "#5A6B75";
const SKY_WASH = "#F2F9FD";

// 2026-10-01 (Dave) — Official wordmark + tagline, shared with the
// marketing LandingScreen so a brand retune only touches one asset.
const LOGO_URL = "https://customer-assets-jai6qajn.emergentagent.net/job_finance-audit-tool-4/artifacts/ehpgga4j_1.png";

// 2026-10-01 (Dave) — Canonical association-type taxonomy for the
// enrollment dropdown.  Mirrored by the Pydantic validator in
// `backend/routes/enrollment.py` — keep both lists in lockstep.
const ASSOCIATION_TYPES = [
  "Homeowners Association (HOA)",
  "Condominium Association",
  "Cooperative",
  "Marina",
  "Country Club",
  "Other",
];

/**
 * Format a raw ZIP input into a strict US `12345` or `12345-6789`
 * shape as the user types:
 *   • Strips every non-digit the user pastes/types.
 *   • Caps at 9 digits (ZIP+4).
 *   • Auto-inserts the hyphen once the 6th digit is reached.
 */
function formatZipCode(raw) {
  const digits = (raw || "").replace(/\D/g, "").slice(0, 9);
  if (digits.length <= 5) return digits;
  return `${digits.slice(0, 5)}-${digits.slice(5)}`;
}


// ---------------------------------------------------------------------------


function ShellFrame({ children, testId }) {
  return (
    <div
      data-testid={testId}
      className="min-h-screen px-5 sm:px-8 py-4 sm:py-6"
      style={{
        background: SKY_WASH,
        color: NAVY,
        fontFamily: "system-ui, -apple-system, 'Segoe UI', sans-serif",
      }}
    >
      <div style={{ maxWidth: 520, margin: "0 auto" }}>
        {/* Slim brand bar — clicking returns to the marketing page. */}
        <div
          data-testid="enroll-brandbar"
          onClick={() => { window.location.hash = ""; }}
          style={{
            display: "inline-flex", alignItems: "center",
            cursor: "pointer", marginBottom: 12,
          }}
        >
          <img
            src={LOGO_URL}
            alt="uReserve Planning — Reserve Fund Planning Reimagined"
            style={{ height: 36, width: "auto", display: "block" }}
          />
        </div>
        {children}
      </div>
    </div>
  );
}


function Field({ id, label, hint, error, children }) {
  return (
    <div style={{ marginBottom: 10 }}>
      <label
        htmlFor={id}
        style={{
          display: "block",
          fontSize: 11, fontWeight: 700, color: NAVY,
          textTransform: "uppercase", letterSpacing: 0.3,
          marginBottom: 4,
        }}
      >
        {label}
      </label>
      {children}
      {hint && !error && (
        <div style={{ fontSize: 11, color: SLATE, marginTop: 3 }}>{hint}</div>
      )}
      {error && (
        <div
          data-testid={`${id}-error`}
          style={{ fontSize: 11, color: CORAL, marginTop: 3, fontWeight: 600 }}
        >{error}</div>
      )}
    </div>
  );
}


const inputStyle = (hasError) => ({
  width: "100%",
  padding: "7px 10px",
  fontSize: 13,
  color: NAVY,
  background: "#FFFFFF",
  border: `1px solid ${hasError ? CORAL : "#C9D6E2"}`,
  borderRadius: 4,
  outline: "none",
  transition: "border-color 120ms ease, box-shadow 120ms ease",
});


function PlanPicker({ plan, onChange, baseFee, proFee }) {
  const opts = [["base", "Base", baseFee], ["pro", "Pro", proFee]];
  return (
    <div style={{ marginBottom: 12 }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: NAVY, textTransform: "uppercase", letterSpacing: 0.3, marginBottom: 4 }}>Choose a Plan</div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
        {opts.map(([key, label, fee]) => {
          const on = plan === key;
          return (
            <button
              key={key}
              type="button"
              onClick={() => onChange(key)}
              data-testid={`enroll-plan-${key}`}
              aria-pressed={on}
              style={{
                textAlign: "left", padding: "8px 12px", borderRadius: 6, cursor: "pointer",
                border: `1.5px solid ${on ? SKY : "#C9D6E2"}`, background: on ? SKY_WASH : "#fff",
                color: NAVY, transition: "border-color 120ms ease, background-color 120ms ease",
              }}
            >
              <div style={{ fontWeight: 700, fontSize: 13 }}>{label}</div>
              <div style={{ fontSize: 12, color: SLATE }}>${fee}/month</div>
            </button>
          );
        })}
      </div>
    </div>
  );
}


// ---------------------------------------------------------------------------
// #enroll — Form
// ---------------------------------------------------------------------------


function EnrollForm() {
  const [values, setValues] = React.useState({
    association_name: "",
    association_type: "",
    address: "",
    city: "",
    state: "",
    zip_code: "",
    admin_full_name: "",
    admin_email: "",
    unit_count: "",
    element_count: "",
  });
  const [errors, setErrors] = React.useState({});
  const [submitting, setSubmitting] = React.useState(false);
  const [serverError, setServerError] = React.useState("");
  // 2026-09-30 (Dave) — Read the admin-controlled monthly fee from
  // the anonymous platform-settings endpoint so the price label in
  // the footer matches whatever's currently configured on the
  // Admin → Payment Settings card.  Fallback to 50 on any error.
  const [monthlyFee, setMonthlyFee] = React.useState(50);
  // 2026-10-01 (Dave) — Demo Mode gate.  When admin flips the toggle,
  // the final "Continue to Payment" click shows a small modal that
  // explains "no card was charged" and stops short of hitting Stripe.
  // Server-side guard in /api/enroll/checkout also enforces this.
  const [demoMode, setDemoMode] = React.useState(false);
  const [showDemoModal, setShowDemoModal] = React.useState(false);
  const [proFee, setProFee] = React.useState(50);
  const [plan, setPlan] = React.useState("base");
  // `#enroll?invite=TOKEN` — admin-issued invite fixes the plan + price and the email.
  const [inviteToken] = React.useState(() => {
    const h = window.location.hash || "";
    const q = h.indexOf("?");
    return q >= 0 ? new URLSearchParams(h.slice(q + 1)).get("invite") : null;
  });
  const [invite, setInvite] = React.useState(null);
  const [inviteError, setInviteError] = React.useState("");
  React.useEffect(() => {
    if (!inviteToken) return;
    const backend = process.env.REACT_APP_BACKEND_URL || "";
    fetch(`${backend}/api/subscribe/invite/${encodeURIComponent(inviteToken)}`)
      .then(async (r) => {
        const d = await r.json().catch(() => ({}));
        if (!r.ok || d.kind !== "new") throw new Error(d.detail || "This invitation link is not valid.");
        if (d.status !== "pending") {
          throw new Error({ used: "This invitation has already been used.", cancelled: "This invitation has been cancelled.", expired: "This invitation has expired. Ask for a new one." }[d.status] || "This invitation is no longer valid.");
        }
        setInvite(d);
        setValues((v) => ({ ...v, admin_email: d.email || "", admin_full_name: d.full_name || v.admin_full_name }));
      })
      .catch((e) => setInviteError(e.message));
  }, [inviteToken]);
  React.useEffect(() => {
    const backend = process.env.REACT_APP_BACKEND_URL || "";
    fetch(`${backend}/api/platform-settings/anonymous`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (d && Number.isFinite(Number(d.monthly_fee_usd))) {
          setMonthlyFee(Number(d.monthly_fee_usd));
        }
        if (d && Number.isFinite(Number(d.pro_fee_usd))) {
          setProFee(Number(d.pro_fee_usd));
        }
        if (d) {
          setDemoMode(Number(d.enrollment_demo_mode) === 1);
        }
      })
      .catch(() => {});
  }, []);

  const set = (k) => (e) => {
    setValues((v) => ({ ...v, [k]: e.target.value }));
    if (errors[k]) setErrors((er) => ({ ...er, [k]: undefined }));
  };

  const validate = () => {
    const er = {};
    if (values.association_name.trim().length < 2) {
      er.association_name = "Please enter your association's name.";
    }
    if (!ASSOCIATION_TYPES.includes(values.association_type)) {
      er.association_type = "Please choose an association type.";
    }
    if (values.address.trim().length < 2) {
      er.address = "Street address is required.";
    }
    if (values.city.trim().length < 2) {
      er.city = "City is required.";
    }
    if (values.state.trim().length < 2) {
      er.state = "State is required.";
    }
    if (!/^\d{5}(-\d{4})?$/.test(values.zip_code.trim())) {
      er.zip_code = "Enter a 5-digit ZIP (e.g. 55123 or 55123-4567).";
    }
    if (values.admin_full_name.trim().length < 2) {
      er.admin_full_name = "Please enter your full name.";
    }
    if (!/^\S+@\S+\.\S+$/.test(values.admin_email.trim())) {
      er.admin_email = "Please enter a valid email address.";
    }
    const n = parseInt(values.unit_count, 10);
    if (!Number.isFinite(n) || n < 1) {
      er.unit_count = "Enter a whole number of members (1 or more).";
    }
    const e = parseInt(values.element_count, 10);
    if (!Number.isFinite(e) || e < 1 || e > 500) {
      er.element_count = "Enter a whole number of elements (1 to 500).";
    }
    return er;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setServerError("");
    if (inviteToken && !invite) return;
    const er = validate();
    setErrors(er);
    if (Object.keys(er).length) return;

    // 2026-10-01 (Dave) — Demo Mode short-circuit.  Show the "no card
    // was charged" modal instead of calling Stripe.  Form state is
    // left intact so the visitor can see what they filled in.
    if (demoMode) {
      setShowDemoModal(true);
      return;
    }

    setSubmitting(true);
    try {
      const backend = process.env.REACT_APP_BACKEND_URL || "";
      const r = await fetch(`${backend}/api/enroll/checkout`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          association_name: values.association_name.trim(),
          association_type: values.association_type,
          address: values.address.trim(),
          city: values.city.trim(),
          state: values.state.trim().toUpperCase(),
          zip_code: values.zip_code.trim(),
          admin_full_name: values.admin_full_name.trim(),
          admin_email: values.admin_email.trim().toLowerCase(),
          unit_count: parseInt(values.unit_count, 10),
          element_count: parseInt(values.element_count, 10),
          origin_url: window.location.origin,
          plan: invite ? "base" : plan,
          ...(invite ? { invite_token: inviteToken } : {}),
        }),
      });
      const data = await r.json();
      if (!r.ok) {
        // Pydantic validation errors come back as `{ detail: [{loc, msg}, ...] }`;
        // custom HTTPException uses `{ detail: "..." }`.
        let msg = "";
        if (Array.isArray(data?.detail)) {
          msg = data.detail.map((d) => d.msg || d.message || "").filter(Boolean).join(" · ");
        } else if (typeof data?.detail === "string") {
          msg = data.detail;
        }
        setServerError(msg || `Request failed (HTTP ${r.status}).`);
        setSubmitting(false);
        return;
      }
      if (!data?.checkout_url) {
        setServerError("Server response missing checkout URL.  Please try again.");
        setSubmitting(false);
        return;
      }
      // Full-page redirect — Stripe hosts checkout on their own domain.
      window.location.href = data.checkout_url;
    } catch (err) {
      setSubmitting(false);
      setServerError(err?.message || "Network error — please try again.");
    }
  };

  return (
    <ShellFrame testId="enroll-form-screen">
      <div style={{
        background: "#FFFFFF",
        border: "1px solid #E2E8EC",
        borderRadius: 8,
        padding: "18px 22px",
        boxShadow: "0 24px 60px rgba(28,78,128,0.10)",
      }}>
        <div style={{
          fontSize: 10, letterSpacing: 0.4, textTransform: "uppercase",
          fontWeight: 700, color: CORAL, marginBottom: 4,
        }}>Start your subscription today</div>
        <h1 style={{
          fontSize: 20, fontWeight: 800, color: NAVY,
          letterSpacing: -0.4, lineHeight: 1.2, margin: "0 0 4px 0",
        }}>Tell us about your association</h1>
        <p style={{ fontSize: 12, color: SLATE, lineHeight: 1.45, margin: "0 0 14px 0" }}>
          A quick 30-second form, then Stripe handles the payment.
          <br />
          We'll email you a link to set your password.
        </p>

        {inviteError && (
          <div data-testid="enroll-invite-error" style={{ background: "#FDF5F2", border: `1px solid ${CORAL}`, color: CORAL, padding: "8px 12px", borderRadius: 4, fontSize: 12, fontWeight: 600, marginBottom: 10 }}>
            {inviteError}
          </div>
        )}
        <form onSubmit={onSubmit} noValidate>
          {invite ? (
            <div data-testid="enroll-invite-plan" style={{ background: SKY_WASH, border: `1px solid ${SKY}`, borderRadius: 6, padding: "8px 12px", marginBottom: 12, fontSize: 12.5, color: NAVY }}>
              Invitation: <strong>{invite.plan_label} Plan</strong> · <strong>${invite.price_usd}/month</strong>
            </div>
          ) : (
            <PlanPicker plan={plan} onChange={setPlan} baseFee={monthlyFee} proFee={proFee} />
          )}
          <Field
            id="association_name"
            label="Association Name"
            error={errors.association_name}
          >
            <input
              id="association_name"
              data-testid="enroll-input-association-name"
              type="text"
              value={values.association_name}
              onChange={set("association_name")}
              autoComplete="organization"
              maxLength={200}
              style={inputStyle(!!errors.association_name)}
              onFocus={(e) => (e.currentTarget.style.borderColor = SKY)}
              onBlur={(e) => (
                e.currentTarget.style.borderColor = errors.association_name ? CORAL : "#C9D6E2"
              )}
            />
          </Field>

          <Field
            id="association_type"
            label="Association Type"
            error={errors.association_type}
          >
            <select
              id="association_type"
              data-testid="enroll-input-association-type"
              value={values.association_type}
              onChange={set("association_type")}
              style={{
                ...inputStyle(!!errors.association_type),
                // native select arrow eats a bit of right padding; also
                // force the font so the placeholder "<Select…>" option
                // isn't rendered in a different browser default.
                appearance: "auto",
                fontFamily: "inherit",
                height: 32,
              }}
              onFocus={(e) => (e.currentTarget.style.borderColor = SKY)}
              onBlur={(e) => (
                e.currentTarget.style.borderColor = errors.association_type ? CORAL : "#C9D6E2"
              )}
            >
              <option value="" disabled>Select…</option>
              {ASSOCIATION_TYPES.map((t) => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>
          </Field>

          <Field
            id="address"
            label="Street Address"
            error={errors.address}
          >
            <input
              id="address"
              data-testid="enroll-input-address"
              type="text"
              value={values.address}
              onChange={set("address")}
              autoComplete="street-address"
              maxLength={200}
              style={inputStyle(!!errors.address)}
              onFocus={(e) => (e.currentTarget.style.borderColor = SKY)}
              onBlur={(e) => (
                e.currentTarget.style.borderColor = errors.address ? CORAL : "#C9D6E2"
              )}
            />
          </Field>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1.4fr 0.6fr 0.9fr",
              gap: 10,
            }}
          >
            <Field id="city" label="City" error={errors.city}>
              <input
                id="city"
                data-testid="enroll-input-city"
                type="text"
                value={values.city}
                onChange={set("city")}
                autoComplete="address-level2"
                maxLength={100}
                style={inputStyle(!!errors.city)}
                onFocus={(e) => (e.currentTarget.style.borderColor = SKY)}
                onBlur={(e) => (
                  e.currentTarget.style.borderColor = errors.city ? CORAL : "#C9D6E2"
                )}
              />
            </Field>
            <Field id="state" label="State" error={errors.state}>
              <input
                id="state"
                data-testid="enroll-input-state"
                type="text"
                value={values.state}
                onChange={(e) => {
                  // State field auto-uppercases as the user types — HOA
                  // legal / mailing convention is UPPER-case abbreviations.
                  setValues((v) => ({ ...v, state: e.target.value.toUpperCase() }));
                  if (errors.state) setErrors((er) => ({ ...er, state: undefined }));
                }}
                autoComplete="address-level1"
                maxLength={50}
                style={{ ...inputStyle(!!errors.state), textTransform: "uppercase" }}
                onFocus={(e) => (e.currentTarget.style.borderColor = SKY)}
                onBlur={(e) => (
                  e.currentTarget.style.borderColor = errors.state ? CORAL : "#C9D6E2"
                )}
              />
            </Field>
            <Field id="zip_code" label="ZIP" error={errors.zip_code}>
              <input
                id="zip_code"
                data-testid="enroll-input-zip"
                type="text"
                inputMode="numeric"
                value={values.zip_code}
                onChange={(e) => {
                  const masked = formatZipCode(e.target.value);
                  setValues((v) => ({ ...v, zip_code: masked }));
                  if (errors.zip_code) setErrors((er) => ({ ...er, zip_code: undefined }));
                }}
                placeholder="12345-6789"
                autoComplete="postal-code"
                maxLength={10}
                style={inputStyle(!!errors.zip_code)}
                onFocus={(e) => (e.currentTarget.style.borderColor = SKY)}
                onBlur={(e) => (
                  e.currentTarget.style.borderColor = errors.zip_code ? CORAL : "#C9D6E2"
                )}
              />
            </Field>
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 10,
            }}
          >
            <Field
              id="admin_full_name"
              label="Your Full Name"
              error={errors.admin_full_name}
            >
              <input
                id="admin_full_name"
                data-testid="enroll-input-admin-name"
                type="text"
                value={values.admin_full_name}
                onChange={set("admin_full_name")}
                autoComplete="name"
                maxLength={120}
                style={inputStyle(!!errors.admin_full_name)}
                onFocus={(e) => (e.currentTarget.style.borderColor = SKY)}
                onBlur={(e) => (
                  e.currentTarget.style.borderColor = errors.admin_full_name ? CORAL : "#C9D6E2"
                )}
              />
            </Field>

            <Field
              id="admin_email"
              label="Email"
              error={errors.admin_email}
            >
              <input
                id="admin_email"
                data-testid="enroll-input-admin-email"
                type="email"
                readOnly={!!invite}
                value={values.admin_email}
                onChange={set("admin_email")}
                autoComplete="email"
                maxLength={200}
                style={inputStyle(!!errors.admin_email)}
                onFocus={(e) => (e.currentTarget.style.borderColor = SKY)}
                onBlur={(e) => (
                  e.currentTarget.style.borderColor = errors.admin_email ? CORAL : "#C9D6E2"
                )}
              />
            </Field>
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 10,
            }}
          >
            <Field
              id="unit_count"
              label="Number of Members"
              error={errors.unit_count}
            >
              <input
                id="unit_count"
                data-testid="enroll-input-unit-count"
                type="number"
                inputMode="numeric"
                min="1"
                max="100000"
                value={values.unit_count}
                onChange={set("unit_count")}
                style={inputStyle(!!errors.unit_count)}
                onFocus={(e) => (e.currentTarget.style.borderColor = SKY)}
                onBlur={(e) => (
                  e.currentTarget.style.borderColor = errors.unit_count ? CORAL : "#C9D6E2"
                )}
              />
            </Field>

            <Field
              id="element_count"
              label="Common Elements (Estimated #)"
              error={errors.element_count}
            >
              <input
                id="element_count"
                data-testid="enroll-input-element-count"
                type="number"
                inputMode="numeric"
                min="1"
                max="500"
                value={values.element_count}
                onChange={set("element_count")}
                style={inputStyle(!!errors.element_count)}
                onFocus={(e) => (e.currentTarget.style.borderColor = SKY)}
                onBlur={(e) => (
                  e.currentTarget.style.borderColor = errors.element_count ? CORAL : "#C9D6E2"
                )}
              />
            </Field>
          </div>

          {serverError && (
            <div
              data-testid="enroll-server-error"
              style={{
                background: "#FDF5F2",
                border: `1px solid ${CORAL}`,
                color: CORAL,
                padding: "8px 12px",
                borderRadius: 4,
                fontSize: 12, fontWeight: 600,
                marginBottom: 10,
              }}
            >{serverError}</div>
          )}

          <button
            type="submit"
            data-testid="enroll-submit"
            disabled={submitting || (!!inviteToken && !invite)}
            style={{
              width: "100%",
              padding: "10px 22px",
              fontSize: 14, fontWeight: 700, letterSpacing: 0.4,
              color: "#fff",
              background: submitting ? "#7A828C" : CORAL,
              border: "none", borderRadius: 999,
              cursor: submitting ? "wait" : "pointer",
              boxShadow: submitting ? "none" : "0 8px 24px rgba(234,106,71,0.35)",
              transition: "transform 120ms ease, background 120ms ease",
            }}
            onMouseEnter={(e) => {
              if (!submitting) e.currentTarget.style.transform = "translateY(-1px)";
            }}
            onMouseLeave={(e) => (e.currentTarget.style.transform = "translateY(0)")}
          >
            {submitting ? "Redirecting to Stripe…" : "Continue to Payment  →"}
          </button>

          <div style={{
            fontSize: 11, color: SLATE, textAlign: "center",
            marginTop: 8, lineHeight: 1.4,
          }}>
            <strong style={{ color: NAVY }} data-testid="enroll-price">${invite ? invite.price_usd : (plan === "pro" ? proFee : monthlyFee)}/month</strong> · Cancel any time · Secure checkout by Stripe
          </div>
        </form>
      </div>
      {showDemoModal && (
        <DemoModeModal onClose={() => setShowDemoModal(false)} />
      )}
    </ShellFrame>
  );
}


/**
 * 2026-10-01 (Dave) — Demo-Mode confirmation modal.
 * Rendered when `enrollment_demo_mode = 1` and the visitor clicks
 * the final Subscribe button.  Zero side effects — no Stripe call,
 * no DB write, no Postmark email.  Pure UX feedback that the form
 * is a drive-test, not a real checkout.
 */
function DemoModeModal({ onClose }) {
  return (
    <div
      data-testid="enroll-demo-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="enroll-demo-modal-title"
      style={{
        position: "fixed", inset: 0,
        background: "rgba(28,78,128,0.55)",
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: 20, zIndex: 9999,
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "#FFFFFF",
          borderRadius: 10,
          maxWidth: 440,
          width: "100%",
          padding: "28px 28px 22px 28px",
          boxShadow: "0 32px 80px rgba(28,78,128,0.35)",
          border: `1px solid ${CORAL}`,
        }}
      >
        <div style={{
          fontSize: 11, letterSpacing: 0.5, textTransform: "uppercase",
          fontWeight: 700, color: CORAL, marginBottom: 6,
        }}>Preview — demo mode</div>
        <h2
          id="enroll-demo-modal-title"
          style={{
            fontSize: 22, fontWeight: 800, color: NAVY,
            letterSpacing: -0.3, lineHeight: 1.2,
            margin: "0 0 10px 0",
          }}
        >Thanks for taking a look.</h2>
        <p style={{ fontSize: 14, lineHeight: 1.55, color: SLATE, margin: "0 0 18px 0" }}>
          We&rsquo;re not quite open for sign-ups yet, so{" "}
          <strong>no card was charged</strong>{" "}
          and no account was created.  Want to know when we go live?
          Drop us a note at{" "}
          <a
            href="mailto:support@ureserveplanning.com"
            style={{ color: NAVY, textDecoration: "underline" }}
          >support@ureserveplanning.com</a>
          {" "}and we&rsquo;ll let you know.
        </p>
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
          <button
            type="button"
            data-testid="enroll-demo-modal-return"
            onClick={() => { window.location.hash = ""; }}
            style={{
              padding: "9px 16px",
              fontSize: 13, fontWeight: 600,
              color: NAVY, background: "#fff",
              border: `1px solid ${NAVY}`, borderRadius: 999,
              cursor: "pointer",
            }}
          >Return to home</button>
          <button
            type="button"
            data-testid="enroll-demo-modal-close"
            onClick={onClose}
            style={{
              padding: "9px 20px",
              fontSize: 13, fontWeight: 700, letterSpacing: 0.3,
              color: "#fff", background: CORAL,
              border: "none", borderRadius: 999,
              cursor: "pointer",
              boxShadow: "0 6px 16px rgba(234,106,71,0.3)",
            }}
          >Close</button>
        </div>
      </div>
    </div>
  );
}


// ---------------------------------------------------------------------------
// #enroll/success — Post-checkout landing page
// ---------------------------------------------------------------------------


function EnrollSuccess() {
  // Parse the session_id out of the hash's query section:
  //   #enroll/success?session_id=cs_test_...
  const [sessionId] = React.useState(() => {
    const h = window.location.hash || "";
    const q = h.split("?")[1] || "";
    const params = new URLSearchParams(q);
    return params.get("session_id") || "";
  });

  return (
    <ShellFrame testId="enroll-success-screen">
      <div style={{
        background: "#FFFFFF",
        border: "1px solid #E2E8EC",
        borderRadius: 8,
        padding: 36,
        boxShadow: "0 24px 60px rgba(28,78,128,0.10)",
        textAlign: "center",
      }}>
        <div style={{
          width: 56, height: 56, borderRadius: "50%",
          background: CORAL, color: "#fff",
          display: "inline-flex", alignItems: "center", justifyContent: "center",
          fontWeight: 800, fontSize: 26,
          margin: "0 auto 16px auto",
        }}>✓</div>
        <div style={{
          fontSize: 11, letterSpacing: 0.4, textTransform: "uppercase",
          fontWeight: 700, color: CORAL, marginBottom: 6,
        }}>Payment received</div>
        <h1 style={{
          fontSize: 28, fontWeight: 800, color: NAVY,
          letterSpacing: -0.6, lineHeight: 1.2, margin: "0 0 12px 0",
        }}>You're in — check your email</h1>
        <p style={{ fontSize: 14, color: SLATE, lineHeight: 1.6, margin: "0 0 24px 0" }}>
          We're setting up your uReserve account right now.  In the next
          minute or two you'll get an email with a temporary password
          and a link to sign in.  From there you'll set your own
          password and land on your dashboard.
        </p>
        <div style={{
          background: SKY_WASH,
          borderLeft: `3px solid ${SKY}`,
          padding: "12px 16px",
          fontSize: 13, color: NAVY, textAlign: "left",
          marginBottom: 24,
        }}>
          <strong>Didn't get the email?</strong> Check spam / promotions,
          or reach out to <a
            href="mailto:support@ureserveplanning.com"
            style={{ color: SKY, textDecoration: "underline" }}
          >support@ureserveplanning.com</a> and we'll re-send it right away.
        </div>
        {sessionId && (
          <div
            data-testid="enroll-success-session-id"
            style={{
              fontSize: 10, color: SLATE, letterSpacing: 0.4,
              fontFamily: "monospace", wordBreak: "break-all",
            }}
          >
            Ref: {sessionId}
          </div>
        )}
      </div>
    </ShellFrame>
  );
}


// ---------------------------------------------------------------------------
// #enroll/cancel — Payment cancelled
// ---------------------------------------------------------------------------


function EnrollCancel() {
  return (
    <ShellFrame testId="enroll-cancel-screen">
      <div style={{
        background: "#FFFFFF",
        border: "1px solid #E2E8EC",
        borderRadius: 8,
        padding: 36,
        boxShadow: "0 24px 60px rgba(28,78,128,0.10)",
        textAlign: "center",
      }}>
        <div style={{
          fontSize: 11, letterSpacing: 0.4, textTransform: "uppercase",
          fontWeight: 700, color: SLATE, marginBottom: 6,
        }}>Checkout cancelled</div>
        <h1 style={{
          fontSize: 26, fontWeight: 800, color: NAVY,
          letterSpacing: -0.6, lineHeight: 1.2, margin: "0 0 12px 0",
        }}>No charge was made</h1>
        <p style={{ fontSize: 14, color: SLATE, lineHeight: 1.6, margin: "0 0 24px 0" }}>
          You cancelled at Stripe before payment completed.  We didn't
          create an account or bill your card.  Ready to try again?
        </p>
        <button
          data-testid="enroll-cancel-retry"
          onClick={() => { window.location.hash = "#enroll"; }}
          style={{
            padding: "12px 28px",
            fontSize: 14, fontWeight: 700, letterSpacing: 0.4,
            color: "#fff", background: CORAL,
            border: "none", borderRadius: 999, cursor: "pointer",
            boxShadow: "0 8px 24px rgba(234,106,71,0.35)",
          }}
        >
          Try Again  →
        </button>
        <div style={{ marginTop: 16 }}>
          <a
            data-testid="enroll-cancel-back"
            onClick={() => { window.location.hash = ""; }}
            style={{
              fontSize: 13, color: NAVY, cursor: "pointer",
              textDecoration: "underline", textDecorationColor: "#C9D6E2",
              textUnderlineOffset: 3,
            }}
          >Back to marketing page</a>
        </div>
      </div>
    </ShellFrame>
  );
}


// ---------------------------------------------------------------------------


/**
 * Route dispatcher — routes on `window.location.hash`.
 *
 *   #enroll                    → <EnrollForm>
 *   #enroll/success?session_id → <EnrollSuccess>
 *   #enroll/cancel             → <EnrollCancel>
 *
 * AppGate calls this component whenever a logged-out visitor lands
 * on any `#enroll*` hash.  The hashchange listener lives in AppGate
 * itself (the existing landing-tick effect), so this component just
 * reads the hash once per render.
 */
export default function EnrollScreen() {
  const hash = (typeof window !== "undefined" ? window.location.hash : "") || "";
  // 2026-10-01 (Dave) — Same escape-hatch as LandingScreen: unlock
  // the shell's `overflow:hidden` + 100vh clamp while the enroll
  // flow is mounted so the form (and success / cancel panels) can
  // scroll naturally on short viewports.
  React.useEffect(() => {
    document.documentElement.classList.add("scroll-page");
    return () => document.documentElement.classList.remove("scroll-page");
  }, []);
  if (hash.startsWith("#enroll/success")) return <EnrollSuccess />;
  if (hash.startsWith("#enroll/cancel")) return <EnrollCancel />;
  return <EnrollForm />;
}

export { EnrollForm, EnrollSuccess, EnrollCancel };
